/**
 * AML operations register (governance backlog C32, decided 2026-10-14: JSON, not records).
 * Same encoding as the source product: a `<!-- AML_REGISTER: {json} -->` block inside the
 * verification's `description`, so registers already written in coreBOS read unchanged.
 * Pure functions: the panel and the tests share them.
 */
export type TxClass = "normale" | "sospetta" | "da_verificare";
export interface Transaction {
  id: string;
  date: string;
  description: string;
  amount: number;
  counterpart: string;
  type: string;
  classification: TxClass;
  notes: string;
}
export interface TransactionRegister { version: 1; transactions: Transaction[]; lastModified: string }

export const TX_TYPES = ["bonifico", "contante", "assegno", "giroconto", "carta", "altro"];
const BLOCK = /<!--\s*AML_REGISTER:\s*([\s\S]*?)\s*-->/;

export function parseRegister(description: unknown): TransactionRegister {
  const empty: TransactionRegister = { version: 1, transactions: [], lastModified: "" };
  const m = typeof description === "string" ? description.match(BLOCK) : null;
  if (!m) return empty;
  try {
    const r = JSON.parse(m[1]) as Partial<TransactionRegister>;
    return { version: 1, transactions: Array.isArray(r.transactions) ? r.transactions : [], lastModified: r.lastModified ?? "" };
  } catch {
    return empty;
  }
}

/** Write the register back, keeping any free text around the block. */
export function serializeRegister(description: unknown, reg: TransactionRegister, now = new Date().toISOString()): string {
  const block = `<!-- AML_REGISTER: ${JSON.stringify({ ...reg, version: 1, lastModified: now })} -->`;
  const text = typeof description === "string" ? description : "";
  if (BLOCK.test(text)) return text.replace(BLOCK, block);
  return text ? `${text}\n${block}` : block;
}

/** The description as people read it: the machine block removed. */
export const stripRegister = (description: string) => description.replace(BLOCK, "").trim();

export function registerStats(reg: TransactionRegister) {
  const t = reg.transactions;
  return {
    total: t.length,
    volume: t.reduce((s, x) => s + (Number(x.amount) || 0), 0),
    suspicious: t.filter((x) => x.classification === "sospetta").length,
    toVerify: t.filter((x) => x.classification === "da_verificare").length,
    // D.Lgs. 231/07 art. 49: cash at or above the threshold is a red flag worth a look
    cashOverThreshold: t.filter((x) => x.type === "contante" && Number(x.amount) >= 5000).length,
  };
}
